import { useState } from 'react';
import { Button } from 'antd';
import {
    EyeInvisibleOutlined,
    EyeOutlined,
    LineChartOutlined,
    LockOutlined,
    PlusOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WalletBalanceValue from '../../../components/WalletBalanceValue/WalletBalanceValue';
import { formatCredits } from '../../../data/home';
import WithdrawModal from './WithdrawModal';

type WalletCardProps = {
    availableCredits?: number;
    reservedCredits?: number;
    hideBalance: boolean;
    loading?: boolean;
    onToggleBalance: () => void;
    onTopUp: () => void;
};

function WalletCard({
    availableCredits = 0,
    reservedCredits = 0,
    hideBalance,
    loading = false,
    onToggleBalance,
    onTopUp,
}: WalletCardProps) {
    const { t } = useTranslation('home');
    const navigate = useNavigate();
    const [withdrawOpen, setWithdrawOpen] = useState(false);

    return (
        <section
            className="wallet-card"
            aria-busy={loading}
            aria-label={loading ? t('walletCard.a11y.loadingBalance') : t('walletCard.a11y.balance')}
        >
            <div className="wallet-top">
                <span>{t('walletCard.availableCredits')}</span>
                <Button
                    type="text"
                    className="wallet-card__eye"
                    icon={hideBalance ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={onToggleBalance}
                    aria-label={hideBalance ? t('walletCard.a11y.showBalance') : t('walletCard.a11y.hideBalance')}
                />
            </div>

            <div className="money">
                <WalletBalanceValue loading={loading} hidden={hideBalance}>
                    {formatCredits(availableCredits)}
                </WalletBalanceValue>
                {!loading ? (
                    <span className="money__unit">
                        {t('walletCard.credits', { count: availableCredits })}
                    </span>
                ) : null}
            </div>
            <div className="credit-rate">{t('walletCard.rate')}</div>
            <div className="reserved">
                <LockOutlined />
                {loading ? (
                    <span className="wallet-balance-skel wallet-balance-skel--line" aria-hidden />
                ) : (
                    t('walletCard.reserved', {
                        amount: hideBalance ? '••' : formatCredits(reservedCredits),
                        count: reservedCredits,
                    })
                )}
            </div>

            <div className="wallet-bottom">
                <div className="wallet-card__actions">
                    <Button type="primary" icon={<PlusOutlined />} onClick={onTopUp}>
                        {t('walletCard.topUp')}
                    </Button>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => setWithdrawOpen(true)}>
                        {t('actions.withdraw')}
                    </Button>
                    <Button type="primary" icon={<LineChartOutlined />} onClick={() => navigate('/activity')}>
                        {t('walletCard.history')}
                    </Button>
                </div>
                <span className="wallet-id">{t('walletCard.brand')}</span>
            </div>

            <WithdrawModal
                open={withdrawOpen}
                availableCredits={availableCredits}
                onClose={() => setWithdrawOpen(false)}
            />
        </section>
    );
}

export default WalletCard;
